'use client';

import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { atom, getDefaultStore } from 'jotai';
import { atomFamily } from 'jotai/utils';

// CDI nudges demo (release-scribe-cdi-nudges-demo). Highlights the note phrase
// a pending Clarifications question quotes. Empty atom = no decorations.

export interface CdiNudgeHighlight {
  quote: string;
  question: string;
  projectId: string;
}

export const DATA_CDI_PROJECT_ID = 'data-cdi-project-id';
export const DATA_CDI_INDEX = 'data-cdi-index';

const CDI_NUDGE_HIGHLIGHT_CLASSES =
  'cdi-nudge-highlight cursor-pointer underline decoration-dashed decoration-accent-amber decoration-2 underline-offset-2';

/** Per-session highlights — no cross-session PHI bleed. */
export const cdiNudgeHighlightsAtomFamily = atomFamily((sessionId: string) => {
  void sessionId;
  return atom<CdiNudgeHighlight[]>([]);
});

export const cdiNudgeHighlightsPluginKey = new PluginKey<{
  sessionId: string;
  decorations: DecorationSet;
}>('cdiNudgeHighlights');

// ponytail: the demo Routine prompt quotes the note phrase in the question;
// add a quote field to ask_user if this ships beyond the demo.
const QUOTE_PATTERN = /["“]([^"”]+)["”]/g;

export const extractQuotes = (question: string): string[] =>
  [...question.matchAll(QUOTE_PATTERN)]
    .map((match) => (match[1] ?? '').trim())
    .filter(Boolean);

type TextPosition = { from: number; to: number };

// Same matching as findTextPositions (case-insensitive, trimmed, per text
// node, overlapping), but the doc is walked and lowercased once per build.
// ponytail: indexOf per quote per node; Aho-Corasick if quote counts grow.
const indexTextNodes = (doc: ProseMirrorNode) => {
  const nodes: { pos: number; text: string }[] = [];
  doc.descendants((node, pos) => {
    if (node.isText) nodes.push({ pos, text: (node.text ?? '').toLowerCase() });
  });
  return nodes;
};

const findInTextNodes = (
  textNodes: { pos: number; text: string }[],
  needle: string
): TextPosition[] => {
  const positions: TextPosition[] = [];
  if (!needle) return positions;
  for (const { pos, text } of textNodes) {
    for (let i = text.indexOf(needle); i !== -1; i = text.indexOf(needle, i + 1)) {
      positions.push({ from: pos + i, to: pos + i + needle.length });
    }
  }
  return positions;
};

export function buildCdiNudgeDecorations(
  doc: ProseMirrorNode,
  highlights: CdiNudgeHighlight[]
): DecorationSet {
  if (highlights.length === 0) return DecorationSet.empty;
  const textNodes = indexTextNodes(doc);
  const positionsByNeedle = new Map<string, TextPosition[]>();
  const positionsFor = (quote: string) => {
    const needle = quote.toLowerCase().trim();
    let positions = positionsByNeedle.get(needle);
    if (!positions) {
      positions = findInTextNodes(textNodes, needle);
      positionsByNeedle.set(needle, positions);
    }
    return positions;
  };
  const decorations = highlights.flatMap(({ quote, projectId }, index) =>
    positionsFor(quote).map(({ from, to }) =>
      Decoration.inline(from, to, {
        class: CDI_NUDGE_HIGHLIGHT_CLASSES,
        [DATA_CDI_PROJECT_ID]: projectId,
        [DATA_CDI_INDEX]: String(index),
      })
    )
  );
  return DecorationSet.create(doc, decorations);
}

const rebuild = (doc: ProseMirrorNode, sessionId: string) => ({
  sessionId,
  decorations: buildCdiNudgeDecorations(
    doc,
    sessionId ? getDefaultStore().get(cdiNudgeHighlightsAtomFamily(sessionId)) : []
  ),
});

export function createCdiNudgeHighlightsPlugin(): Plugin {
  return new Plugin({
    key: cdiNudgeHighlightsPluginKey,
    state: {
      init: () => ({ sessionId: '', decorations: DecorationSet.empty }),
      apply: (tr, value) => {
        const meta: unknown = tr.getMeta(cdiNudgeHighlightsPluginKey);
        if (typeof meta === 'string') return rebuild(tr.doc, meta);
        // Rebuild, not map: a full setContent (note regen, remote sync)
        // replaces every node, and mapping would drop the highlight for good.
        // ponytail: one doc walk per edit while a nudge shows; map plus a
        // full-replace check if large notes make typing slow.
        if (tr.docChanged && value.sessionId) return rebuild(tr.doc, value.sessionId);
        return value;
      },
    },
    props: {
      decorations: (state) => cdiNudgeHighlightsPluginKey.getState(state)?.decorations,
    },
  });
}
